import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';

import { AuthService } from '../../modules/auth/services';
import { isPublicEndpoint } from '../enums/public-endpoints.enum';

const RETRY_HEADER = 'X-GextorPro-Auth-Retry';
let refreshRequest$: Observable<string> | null = null;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const accessToken = authService.getAccessToken();
  const authenticatedRequest =
    accessToken && !isPublicEndpoint(request.url)
      ? request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : request;

  return next(authenticatedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      const canRefresh =
        error.status === 401 &&
        !isPublicEndpoint(request.url) &&
        !request.headers.has(RETRY_HEADER);

      if (!canRefresh) {
        return throwError(() => error);
      }

      const currentToken = authService.getAccessToken();
      if (currentToken && currentToken !== accessToken) {
        return next(
          request.clone({
            setHeaders: { Authorization: `Bearer ${currentToken}`, [RETRY_HEADER]: '1' }
          })
        );
      }

      if (!refreshRequest$) {
        refreshRequest$ = authService.refreshToken().pipe(
          catchError((refreshError: HttpErrorResponse) => {
            authService.logout(false);
            return throwError(() => refreshError);
          }),
          finalize(() => {
            refreshRequest$ = null;
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
      }

      return refreshRequest$.pipe(
        switchMap((newAccessToken) =>
          next(
            request.clone({
              setHeaders: {
                Authorization: `Bearer ${newAccessToken}`,
                [RETRY_HEADER]: '1'
              }
            })
          )
        )
      );
    })
  );
};
